import { optionalQueryParams } from "../apiEndpointHelpers";
import type { ApiEndpointBuilder } from "../apiEndpointHelpers";

import type {
  AdminV2GrowthOrganisationOptionsResponse,
  AdminV2GrowthSegmentEntityType,
  AdminV2GrowthSegmentFilterDefinition,
  AdminV2GrowthSegmentPreviewByIdResponse,
  AdminV2GrowthSegmentPreviewByRulesResponse,
  AdminV2GrowthSegmentRecipientsResponse,
  AdminV2GrowthSegmentRecomputeJobResponse,
  AdminV2GrowthSegmentRecordResponse,
  AdminV2GrowthSegmentsListResponse,
  AdminV2GrowthSegmentStatus,
  JsonValue as AdminV2JsonValue,
  AdminV2MessagingCategory,
} from "@/types/adminV2";

export const buildAdminV2GrowthSegmentsEndpoints = (
  builder: ApiEndpointBuilder,
) => ({
  getAdminV2GrowthOrganisationOptions: builder.query<
    AdminV2GrowthOrganisationOptionsResponse,
    { q?: string; limit?: number; offset?: number }
  >({
    query: (params) => ({
      url: "/admin/v2/growth/segments/organisation-options",
      params: optionalQueryParams(params),
      suppressAccessDeniedModal: true,
    }),
    keepUnusedDataFor: 0,
  }),
  getAdminV2GrowthSegments: builder.query<
    AdminV2GrowthSegmentsListResponse,
    {
      organisationId?: string;
      entityType?: AdminV2GrowthSegmentEntityType;
      dashboardChipOnly?: boolean;
      q?: string;
      status?: AdminV2GrowthSegmentStatus;
      limit?: number;
      offset?: number;
    } | void
  >({
    query: (params) => ({
      url: "/admin/v2/growth/segments",
      params: optionalQueryParams(params),
      // This list powers the "audiences saved filters" convenience pull on
      // shared CRM list pages (contacts, users, partners, organisations) as
      // well as the dedicated audiences/segments workspace. On those shared
      // pages a 403 for roles without `audiences.view`/`segments.manage`
      // (e.g. Support Lead) must degrade inline — the filters simply do not
      // show — instead of firing the global access-denied modal on every
      // navigation. The dedicated audiences route stays guarded by its own
      // route gate (see apps/admin/src/routing/adminRoutes.tsx — the
      // `v2_audiences` route `requiredPermission`/`requiredPermissionFallbacks`),
      // so suppressing the modal here never weakens real access control.
      // See `shouldPublishAdminAccessDeniedEvent` in
      // apps/admin/src/store/api/baseQueryWithAuth.ts.
      suppressAccessDeniedModal: true,
    }),
    providesTags: [{ type: "AdminV2GrowthSegments", id: "LIST" }],
    keepUnusedDataFor: 20,
  }),

  createAdminV2GrowthSegment: builder.mutation<
    AdminV2GrowthSegmentRecordResponse,
    {
      organisationId: string;
      name: string;
      description?: string | null;
      entityType?: AdminV2GrowthSegmentEntityType;
      dashboardChipEnabled?: boolean;
      filterDefinition?: AdminV2GrowthSegmentFilterDefinition | null;
      rules: AdminV2JsonValue;
      sourceActionScopeId?: string;
      sourceActionScopeDigest?: string;
    }
  >({
    query: (body) => ({
      url: "/admin/v2/growth/segments",
      method: "POST",
      data: body,
    }),
    invalidatesTags: [
      { type: "AdminV2GrowthSegments", id: "LIST" },
      { type: "AdminV2AuditLog", id: "LIST" },
    ],
  }),

  getAdminV2GrowthSegment: builder.query<
    AdminV2GrowthSegmentRecordResponse,
    { segmentId: string }
  >({
    query: ({ segmentId }) => ({
      url: `/admin/v2/growth/segments/${segmentId}`,
    }),
    providesTags: (_result, _error, { segmentId }) => [
      { type: "AdminV2GrowthSegments", id: segmentId },
    ],
    keepUnusedDataFor: 20,
  }),

  updateAdminV2GrowthSegment: builder.mutation<
    AdminV2GrowthSegmentRecordResponse,
    {
      segmentId: string;
      body: {
        name?: string;
        description?: string | null;
        entityType?: AdminV2GrowthSegmentEntityType;
        dashboardChipEnabled?: boolean;
        filterDefinition?: AdminV2GrowthSegmentFilterDefinition | null;
        status?: AdminV2GrowthSegmentStatus;
        rules?: AdminV2JsonValue;
      };
    }
  >({
    query: ({ segmentId, body }) => ({
      url: `/admin/v2/growth/segments/${segmentId}`,
      method: "PUT",
      data: body,
    }),
    invalidatesTags: (_result, _error, { segmentId }) => [
      { type: "AdminV2GrowthSegments", id: "LIST" },
      { type: "AdminV2GrowthSegments", id: segmentId },
      { type: "AdminV2AuditLog", id: "LIST" },
    ],
  }),

  publishAdminV2GrowthSegment: builder.mutation<
    AdminV2GrowthSegmentRecordResponse,
    { segmentId: string }
  >({
    query: ({ segmentId }) => ({
      url: `/admin/v2/growth/segments/${segmentId}/publish`,
      method: "POST",
      data: {},
    }),
    invalidatesTags: (_result, _error, { segmentId }) => [
      { type: "AdminV2GrowthSegments", id: "LIST" },
      { type: "AdminV2GrowthSegments", id: segmentId },
      { type: "AdminV2AuditLog", id: "LIST" },
    ],
  }),

  archiveAdminV2GrowthSegment: builder.mutation<
    AdminV2GrowthSegmentRecordResponse,
    { segmentId: string }
  >({
    query: ({ segmentId }) => ({
      url: `/admin/v2/growth/segments/${segmentId}/archive`,
      method: "POST",
      data: {},
    }),
    invalidatesTags: (_result, _error, { segmentId }) => [
      { type: "AdminV2GrowthSegments", id: "LIST" },
      { type: "AdminV2GrowthSegments", id: segmentId },
      { type: "AdminV2AuditLog", id: "LIST" },
    ],
  }),

  reactivateAdminV2GrowthSegment: builder.mutation<
    AdminV2GrowthSegmentRecordResponse,
    { segmentId: string }
  >({
    query: ({ segmentId }) => ({
      url: `/admin/v2/growth/segments/${segmentId}/reactivate`,
      method: "POST",
      data: {},
    }),
    invalidatesTags: (_result, _error, { segmentId }) => [
      { type: "AdminV2GrowthSegments", id: "LIST" },
      { type: "AdminV2GrowthSegments", id: segmentId },
      { type: "AdminV2AuditLog", id: "LIST" },
    ],
  }),

  demoteAdminV2GrowthSegment: builder.mutation<
    AdminV2GrowthSegmentRecordResponse,
    { segmentId: string }
  >({
    query: ({ segmentId }) => ({
      url: `/admin/v2/growth/segments/${segmentId}/demote`,
      method: "POST",
      data: {},
    }),
    invalidatesTags: (_result, _error, { segmentId }) => [
      { type: "AdminV2GrowthSegments", id: "LIST" },
      { type: "AdminV2GrowthSegments", id: segmentId },
      { type: "AdminV2AuditLog", id: "LIST" },
    ],
  }),

  previewAdminV2GrowthSegmentById: builder.mutation<
    AdminV2GrowthSegmentPreviewByIdResponse,
    {
      category?: AdminV2MessagingCategory;
      localFilterRules?: AdminV2JsonValue;
      localFilterSignature?: string;
      segmentId: string;
      sampleLimit?: number;
    }
  >({
    query: ({ segmentId, ...body }) => ({
      url: `/admin/v2/growth/segments/${segmentId}/preview`,
      method: "POST",
      data: body,
    }),
  }),

  previewAdminV2GrowthSegmentRules: builder.mutation<
    AdminV2GrowthSegmentPreviewByRulesResponse,
    {
      organisationId: string;
      sampleLimit?: number;
    } & (
      | {
          rules: AdminV2JsonValue;
          filterDefinition?: never;
          entityType?: never;
        }
      | {
          rules?: never;
          filterDefinition: AdminV2GrowthSegmentFilterDefinition;
          entityType: AdminV2GrowthSegmentEntityType;
        }
    )
  >({
    query: (body) => ({
      url: "/admin/v2/growth/segments/preview",
      method: "POST",
      data: body,
    }),
  }),

  getAdminV2GrowthSegmentRecipients: builder.query<
    AdminV2GrowthSegmentRecipientsResponse,
    {
      segmentId: string;
      category?: AdminV2MessagingCategory;
      localFilterRules?: AdminV2JsonValue;
      q?: string;
      status?: AdminV2GrowthSegmentRecipientsResponse["recipients"][number]["recipientStatus"];
      limit?: number;
      offset?: number;
    }
  >({
    query: ({ segmentId, category, localFilterRules, ...params }) => ({
      url: `/admin/v2/growth/segments/${segmentId}/recipients${category !== undefined || localFilterRules !== undefined ? "/preview" : ""}`,
      ...(category !== undefined || localFilterRules !== undefined
        ? { method: "POST", data: { category, localFilterRules } }
        : {}),
      params: optionalQueryParams(params),
    }),
    providesTags: (_result, _error, { segmentId }) => [
      { type: "AdminV2GrowthSegments", id: segmentId },
    ],
    keepUnusedDataFor: 10,
  }),

  recomputeAdminV2GrowthSegment: builder.mutation<
    AdminV2GrowthSegmentRecomputeJobResponse,
    { segmentId: string }
  >({
    query: ({ segmentId }) => ({
      url: `/admin/v2/growth/segments/${segmentId}/recompute`,
      method: "POST",
      data: {},
    }),
    invalidatesTags: [
      { type: "AdminV2Jobs", id: "LIST" },
      { type: "AdminV2AuditLog", id: "LIST" },
    ],
  }),
});
